import React, { useContext, useState, useMemo } from 'react';
import { PageTitle, Card, EmptyState, Button } from '../components/common';
import { AuthContext } from '../contexts/AuthContext';
import { mockCertificates, mockCourses } from '../mockData';
import { Certificate } from '../types';

const ViewIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>;
const DownloadIconFile = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>;
const XIcon = () => <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"></path></svg>;
const ChevronLeftIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>;
const ChevronRightIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>;

const CERTIFICATES_PER_PAGE = 10;

const CertificatePreviewModal: React.FC<{
    certificate: Certificate | null;
    onClose: () => void;
    onDownload: (certificate: Certificate) => void;
}> = ({ certificate, onClose, onDownload }) => {
    const auth = useContext(AuthContext);
    const course = useMemo(() => mockCourses.find(c => c.id === certificate?.courseId), [certificate]);

    if (!certificate || !course) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl" onClick={e => e.stopPropagation()}>
                <header className="flex justify-between items-center p-4 border-b">
                    <h3 className="text-lg font-bold text-gray-800">Certificate Preview</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200"><XIcon /></button>
                </header>
                <div className="p-8 text-center bg-gray-50">
                    <div className="border-4 border-primary p-8 rounded-lg">
                        <h1 className="text-3xl font-bold text-primary">Certificate of Completion</h1>
                        <p className="mt-4 text-gray-600">This is to certify that</p>
                        <p className="text-2xl font-semibold my-4 text-gray-900">{auth?.currentUser?.firstName} {auth?.currentUser?.lastName}</p>
                        <p className="text-gray-600">has successfully completed the course</p>
                        <p className="text-xl font-semibold my-2 text-gray-800">{course.title}</p>
                        <p className="text-sm text-gray-500 mt-6">Issued on: {new Date(certificate.issueDate).toLocaleDateString()}</p>
                    </div>
                </div>
                <footer className="p-4 border-t flex justify-end gap-2">
                    <Button variant="secondary" onClick={onClose}>Close</Button>
                    <Button onClick={() => onDownload(certificate)}><DownloadIconFile /> Download PDF</Button>
                </footer>
            </div>
        </div>
    );
};

const Pagination: React.FC<{ currentPage: number; totalPages: number; onPageChange: (page: number) => void }> = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

    return (
        <div className="flex flex-wrap justify-between items-center mt-6 px-6 pb-4 gap-4">
            <p className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
            </p>
            <nav className="flex items-center space-x-1">
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 text-gray-500"
                    aria-label="Previous page"
                >
                    <ChevronLeftIcon />
                </button>
                {pageNumbers.map(number => (
                    <button
                        key={number}
                        onClick={() => onPageChange(number)}
                        className={`w-9 h-9 text-sm font-medium rounded-full flex items-center justify-center transition-colors ${currentPage === number ? 'bg-primary text-white shadow-sm' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                        aria-current={currentPage === number ? 'page' : undefined}
                    >
                        {number}
                    </button>
                ))}
                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 text-gray-500"
                    aria-label="Next page"
                >
                    <ChevronRightIcon />
                </button>
            </nav>
        </div>
    );
};


const MyCertificatesPage: React.FC = () => {
    const auth = useContext(AuthContext);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);

    const userCertificates = mockCertificates.filter(cert => cert.userId === auth?.currentUser?.id);

    const totalPages = Math.ceil(userCertificates.length / CERTIFICATES_PER_PAGE);

    const paginatedCertificates = useMemo(() => {
        const startIndex = (currentPage - 1) * CERTIFICATES_PER_PAGE;
        return userCertificates.slice(startIndex, startIndex + CERTIFICATES_PER_PAGE);
    }, [userCertificates, currentPage]);

    const handleDownload = (cert: Certificate) => {
        const course = mockCourses.find(c => c.id === cert.courseId);
        const userName = `${auth?.currentUser?.firstName} ${auth?.currentUser?.lastName}`;
        const courseTitle = course?.title || 'Unknown Course';
        const issueDate = new Date(cert.issueDate).toLocaleDateString();

        const content = `
            Certificate of Completion

            This is to certify that
            ${userName}
            has successfully completed the course
            ${courseTitle}

            Issued on: ${issueDate}
            Marks: ${cert.marks}/${cert.outOf}
        `;
        const blob = new Blob([content], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Certificate-${courseTitle.replace(/\s+/g, '-')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div>
            <PageTitle title="My Certificates" />

            {userCertificates.length > 0 ? (
                <Card className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-500">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 font-semibold">
                                <tr>
                                    <th scope="col" className="px-6 py-4">ID</th>
                                    <th scope="col" className="px-6 py-4">Certificate Name</th>
                                    <th scope="col" className="px-6 py-4">Date</th>
                                    <th scope="col" className="px-6 py-4">Marks</th>
                                    <th scope="col" className="px-6 py-4">Out of</th>
                                    <th scope="col" className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedCertificates.map((cert, index) => {
                                    const course = mockCourses.find(c => c.id === cert.courseId);
                                    const certificateName = course ? `${course.title} Certificate` : 'Course Certificate';

                                    return (
                                        <tr key={cert.id} className="bg-white border-b last:border-b-0 hover:bg-gray-50">
                                            <td className="px-6 py-4 font-medium text-gray-700">
                                                {((currentPage - 1) * CERTIFICATES_PER_PAGE + index + 1).toString().padStart(2, '0')}
                                            </td>
                                            <td className="px-6 py-4 font-bold text-gray-800 whitespace-nowrap">
                                                {certificateName}
                                            </td>
                                            <td className="px-6 py-4 text-gray-600">
                                                {new Date(cert.issueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                            </td>
                                            <td className="px-6 py-4 text-gray-600">{cert.marks}</td>
                                            <td className="px-6 py-4 text-gray-600">{cert.outOf}</td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end space-x-4">
                                                    <button onClick={() => setSelectedCertificate(cert)} className="text-gray-500 hover:text-primary" aria-label={`View certificate for ${certificateName}`}>
                                                        <ViewIcon />
                                                    </button>
                                                    <button onClick={() => handleDownload(cert)} className="text-gray-500 hover:text-primary" aria-label={`Download certificate for ${certificateName}`}>
                                                        <DownloadIconFile />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                     {totalPages > 1 && (
                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                    )}
                </Card>
            ) : (
                <EmptyState
                    title="No Certificates Yet"
                    message="You'll find your certificates here once you complete a course."
                />
            )}

            <CertificatePreviewModal
                certificate={selectedCertificate}
                onClose={() => setSelectedCertificate(null)}
                onDownload={handleDownload}
            />
        </div>
    );
};

export default MyCertificatesPage;